import React, { useState } from 'react';
import { useCalendarData } from './CalendarDataContext';

/**
 * "Report a problem", on every page that shows county information.
 *
 * The owner is the only person who can fix a wrong date, and a subscriber
 * looking at a wrong date is the only person who knows it is wrong. This is the
 * shortest path between the two. It emails the owner with the county and the
 * page, so the report arrives already knowing what it is about.
 */
function ReportProblem({ countyId = null, stateCode = null, page }) {
  const { api, canReport } = useCalendarData();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setError('');
    setBusy(true);
    try {
      await api('/report', {
        method: 'POST',
        body: JSON.stringify({ countyId, stateCode, page, message })
      });
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Checked before `sent` so that a surface which cannot report never shows the
  // confirmation either. Both apps set this true today; see CalendarDataContext
  // for why it is a value rather than an assumption.
  if (!canReport) return null;

  if (sent) {
    return (
      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: '16px' }}>
        Thank you. We will check the county source and correct it.
      </p>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          background: 'none',
          border: 'none',
          padding: '8px 0',
          color: 'var(--color-text-muted)',
          fontSize: '0.8rem',
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
      >
        Report a problem on this page
      </button>
    );
  }

  return (
    <div style={{ marginTop: '16px' }}>
      <label
        htmlFor={`report-${page}`}
        style={{ display: 'block', fontSize: '0.8rem', color: 'var(--color-text-light)', marginBottom: '6px' }}
      >
        What looks wrong? We will check it against the county source.
      </label>
      <textarea
        id={`report-${page}`}
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={3}
        style={{
          width: '100%',
          padding: '10px',
          fontSize: '16px',
          borderRadius: '6px',
          border: '1px solid var(--color-border)',
          background: 'var(--color-input-bg, var(--color-surface))',
          color: 'var(--color-text)',
          boxSizing: 'border-box'
        }}
      />
      {error && (
        <p style={{ color: 'var(--color-danger-text, var(--color-text))', fontSize: '0.8rem' }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button
          className="btn btn-primary"
          onClick={send}
          disabled={busy || message.trim() === ''}
        >
          {busy ? 'Sending' : 'Send'}
        </button>
        <button className="btn btn-secondary" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default ReportProblem;
